import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { ConfirmProvider } from "@plerity/ui-core";
import {
  BarChart3,
  FileText,
  Filter,
  FormInput,
  List,
  Palette,
  Send,
  Settings,
  Shield,
  Tag,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { OrgSwitcher } from "./components/OrgSwitcher";
import { useAdminStore } from "./lib/admin";
import { useTenantStore } from "./lib/tenant";
import { EmailSettingsTabContent } from "./routes/settings/SettingsTabContent";

// Single title-less ("standalone") section — SidebarNav renders a section with
// an empty title as flat top-level nav links with no collapsible group header
// (see SectionGroup in SidebarNav.tsx, which omits the header when `title` is
// falsy). Every visible page therefore appears as a flat sidebar link with no
// "Campaigns" / "Automations" / "Settings" clustering. Hidden detail/edit/import
// routes stay registered (still routable) but are kept next to their logical
// parent route for maintainability.
const sidebarGroups: SidebarGroup[] = [
  {
    title: "",
    routes: [
      // ── Dashboard ──────────────────────────────────────────────────────
      {
        path: "email/overview",
        label: "Overview",
        icon: BarChart3,
        loader: () => import("./routes/email/EmailOverview"),
      },
      {
        path: "email/analytics",
        label: "Analytics",
        icon: TrendingUp,
        loader: () => import("./routes/email/EmailAnalytics"),
      },
      // ── Audience ───────────────────────────────────────────────────────
      // Consolidates the former four-entry "Lists & Contacts" section into a
      // single "Audience" hub link.
      // Plan: agents/plans/email_audience_consolidation_plan_2026-06-12.md
      {
        path: "email/audience",
        label: "Audience",
        icon: Users,
        loader: () => import("./routes/email/Audience"),
      },
      // Detail / import pages — full-page routes reached from within the
      // Audience tabs (hidden from the sidebar).
      {
        path: "email/lists/:listId",
        label: "List Detail",
        icon: List,
        loader: () => import("./routes/email/ListDetail"),
        hidden: true,
      },
      {
        path: "email/contacts/:contactId",
        label: "Contact Detail",
        icon: Users,
        loader: () => import("./routes/email/ContactDetail"),
        hidden: true,
      },
      {
        path: "email/contacts/import",
        label: "Import Contacts",
        icon: Users,
        loader: () => import("./routes/email/ContactImport"),
        hidden: true,
      },
      // Legacy deep-link redirects → /email/audience?tab=… (hidden).
      {
        path: "email/lists",
        label: "Lists",
        icon: List,
        loader: () => import("./routes/email/Lists"),
        hidden: true,
      },
      {
        path: "email/contacts",
        label: "Contacts",
        icon: Users,
        loader: () => import("./routes/email/Contacts"),
        hidden: true,
      },
      {
        path: "email/segments",
        label: "Segments",
        icon: Filter,
        loader: () => import("./routes/email/Segments"),
        hidden: true,
      },
      {
        path: "email/tags",
        label: "Tags",
        icon: Tag,
        loader: () => import("./routes/email/Tags"),
        hidden: true,
      },
      // ── Campaigns ──────────────────────────────────────────────────────
      {
        path: "email/campaigns",
        label: "Campaigns",
        icon: Send,
        loader: () => import("./routes/email/Campaigns"),
      },
      {
        path: "email/campaigns/new",
        label: "New Campaign",
        icon: Send,
        loader: () => import("./routes/email/CampaignBuilder"),
        hidden: true,
      },
      {
        path: "email/campaigns/:campaignId/edit",
        label: "Edit Campaign",
        icon: Send,
        loader: () => import("./routes/email/CampaignBuilder"),
        hidden: true,
      },
      {
        path: "email/campaigns/:campaignId/report",
        label: "Campaign Report",
        icon: Send,
        loader: () => import("./routes/email/CampaignReport"),
        hidden: true,
      },
      {
        path: "email/campaigns/:campaignId/send",
        label: "Send Campaign",
        icon: Send,
        loader: () => import("./routes/email/CampaignSendWizard"),
        hidden: true,
      },
      // ── Templates ──────────────────────────────────────────────────────
      {
        path: "email/templates",
        label: "Templates",
        icon: FileText,
        loader: () => import("./routes/email/Templates"),
      },
      {
        path: "email/templates/new",
        label: "New Template",
        icon: FileText,
        loader: () => import("./routes/email/TemplateEditor"),
        hidden: true,
      },
      {
        path: "email/templates/:templateId/edit",
        label: "Edit Template",
        icon: FileText,
        loader: () => import("./routes/email/TemplateEditor"),
        hidden: true,
      },
      // ── Automations ────────────────────────────────────────────────────
      {
        path: "email/automations",
        label: "Automations",
        icon: Zap,
        loader: () => import("./routes/email/Automations"),
      },
      {
        path: "email/automations/new",
        label: "New Automation",
        icon: Zap,
        loader: () => import("./routes/email/AutomationBuilder"),
        hidden: true,
      },
      {
        path: "email/automations/:automationId/edit",
        label: "Edit Automation",
        icon: Zap,
        loader: () => import("./routes/email/AutomationBuilder"),
        hidden: true,
      },
      {
        path: "email/automations/:automationId/stats",
        label: "Automation Stats",
        icon: Zap,
        loader: () => import("./routes/email/AutomationStats"),
        hidden: true,
      },
      // ── Deliverability ─────────────────────────────────────────────────
      {
        path: "email/deliverability",
        label: "Deliverability",
        icon: Shield,
        loader: () => import("./routes/email/Deliverability"),
      },
      // ── Forms ──────────────────────────────────────────────────────────
      {
        path: "email/forms",
        label: "Forms",
        icon: FormInput,
        loader: () => import("./routes/email/Forms"),
      },
      {
        path: "email/forms/new",
        label: "New Form",
        icon: FormInput,
        loader: () => import("./routes/email/FormBuilder"),
        hidden: true,
      },
      {
        path: "email/forms/:formId/edit",
        label: "Edit Form",
        icon: FormInput,
        loader: () => import("./routes/email/FormBuilder"),
        hidden: true,
      },
      {
        path: "email/forms/:formId/submissions",
        label: "Form Submissions",
        icon: FormInput,
        loader: () => import("./routes/email/FormSubmissions"),
        hidden: true,
      },
      {
        path: "email/forms/:formId/analytics",
        label: "Form Analytics",
        icon: FormInput,
        loader: () => import("./routes/email/FormAnalytics"),
        hidden: true,
      },
      // ── Brand Settings ─────────────────────────────────────────────────
      {
        path: "email/brand",
        // Sidebar label matches the route heading ("Brand Settings") for
        // consistency — P5 of visual polish 2026-05-20.
        label: "Brand Settings",
        icon: Palette,
        loader: () => import("./routes/email/BrandSettings"),
      },
      // Hidden legacy settings redirect. The unified settings page is now
      // reached via the top-right avatar → Settings (auto-mounted by
      // createShell() via `config.settings` at the canonical path `/settings`).
      // This hidden route keeps the old /email/settings deep-link / bookmark
      // working by redirecting to /settings.
      // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
      {
        path: "email/settings",
        label: "Settings (redirect)",
        icon: Settings,
        loader: () => import("@plerity/dashboard-shell/SettingsRedirect"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/email";

/**
 * Header action buttons rendered in the shell's top-right area.
 *
 * createShell's AuthUserMenuBridge (createShell.tsx ~line 320) renders
 * our `headerActions` BEFORE its own auto-injected `<UserMenu />` (the
 * avatar dropdown with theme selector / settings / security / sign-out)
 * and `<MobileMenuButton />`. We therefore pass ONLY the email-specific
 * super-admin org switcher here — never ThemeToggle or MobileMenuButton,
 * as they would be visible duplicates of the shell defaults. See
 * `frontend/apps/seo/src/components/HeaderActions.tsx` and
 * `frontend/apps/social/src/App.tsx` for the canonical pattern.
 */
function HeaderActions() {
  const isSuperAdmin = useAdminStore((s) => s.isSuperAdmin);
  return isSuperAdmin ? <OrgSwitcher /> : null;
}

/**
 * TenantContextLoader — Loads tenant context and admin status on mount.
 *
 * Unified auth (via createShell({ auth })) handles authentication.
 * This component only handles the tenant-specific context loading
 * (org switcher, super admin detection) that the email app needs.
 */
function TenantContextLoader({ children }: { children: React.ReactNode }) {
  const fetchTenantContext = useTenantStore((s) => s.fetchTenantContext);
  const fetchOrganizations = useAdminStore((s) => s.fetchOrganizations);
  const setIsSuperAdmin = useAdminStore((s) => s.setIsSuperAdmin);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchTenantContext()
      .then(() =>
        fetchOrganizations()
          .then(() => setIsSuperAdmin(true))
          .catch((err: unknown) => {
            // 403 = not a super-admin (hide the org switcher)
            // 401 = session expired (preserve previous super-admin state so
            //        the org switcher stays visible and shows stale data —
            //        the next real API call will trigger the auth redirect)
            // createFetcher rejects with a FLAT normalized error (.status), not
            // the raw axios `.response.status` shape — read the flat status.
            const status = err && typeof err === "object" ? (err as { status?: number }).status : undefined;
            if (status === 403) {
              setIsSuperAdmin(false);
            }
            // On 401 or network error, leave isSuperAdmin as-is
            // (persisted value from previous successful session)
          })
      )
      .finally(() => setLoaded(true));
  }, [fetchTenantContext, fetchOrganizations, setIsSuperAdmin]);

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}

const emailSettingsTabs: SettingsTab[] = [
  {
    id: "email-settings",
    label: "Email Settings",
    icon: Settings,
    content: <EmailSettingsTabContent />,
  },
];

const Shell = createShell({
  name: "email",
  displayName: "Email Marketing",
  shortTitle: "Email",
  sidebarGroups,
  defaultRoute: "/email/overview",
  currentApp: "email",
  basename,
  // HeaderActions is a component — as ReactNode it renders dynamically inside Shell
  // (which is inside AppShell, which provides QueryClientProvider context).
  headerActions: <HeaderActions />,
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  settingsRoute: "/settings",
  auth: {
    required: true,
    entitlement: "email",
  },
  chat: {
    enabled: true,
    features: ["campaigns", "lists", "contacts", "segments", "templates", "automations", "analytics", "sender-domains"],
  },
  testerNotes: {
    enabled: true,
    contextId: "web-email",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes / Assistant / Privacy
  // are auto-included by UserSettingsPage; we inject the email-specific tab here.
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
  settings: {
    additionalTabs: emailSettingsTabs,
    assistantName: "Email Assistant",
  },
});

/**
 * App root.
 *
 * Auth is handled entirely by createShell({ auth: { required: true, entitlement: 'email',
 * loginUrl: ... } }) — the shared AuthGuard redirects unauthenticated users to Portal login.
 * The legacy SSO OAuth callback routes (/auth/callback, /auth/login) have been removed
 * as part of the unified auth migration (Phase 4).
 */
function App() {
  return (
    <TenantContextLoader>
      {/*
        createShell({ auth }) handles authentication — Shell includes AuthGuard.
        The chat assistant is rendered by the shell's floating-widget slot
        (driven by chat.enabled above + the user's bottom-right widget
        preference: assistant / tester-notes / none). A standalone <ChatWidget>
        here previously rendered a SECOND, always-on widget that duplicated the
        shell's and ignored that preference — removed 2026-06-15.

        ConfirmProvider supplies the app-wide imperative useConfirm() dialog
        (replaces window.confirm) — one instance near the root serves every
        route.
      */}
      <ConfirmProvider>
        <Shell />
      </ConfirmProvider>
    </TenantContextLoader>
  );
}

export default App;
